import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { areaStore } from "../../stores/locales/area_store"
import { getCsrfToken } from "shared/form_helpers"

/**
 * Sources print-mapping status for the Visual Print Mapper and drives the floor plan
 * icon manager's mapping-status colors + hide-mapped filter, and owns the legend/toggle
 * UI above the floor plan.
 *
 * Connects to data-controller="print-mapper--mapping-status".
 *
 * Responsibilities:
 * - Subscribes to areaStore. On area change, fetches
 *   `GET /api/print_mappings/workstations?area_id=<id>`, computes the set of workstation
 *   ids whose `print_mappings` array is non-empty, and pushes them to the icon manager
 *   via setMappedDeviceIds(...). A request-id guard ignores stale responses.
 * - Listens for bubbling `floorplan:mapping-created` and `floorplan:mapping-deleted`
 *   events on this.element and updates the affected workstation's mapped state LOCALLY
 *   from the event's `workstation` payload (add/remove one id), so icon colors + the
 *   hide filter update live without a full-area refetch. A guarded full refetch remains
 *   only as a fallback when the event carries no workstation payload.
 * - Owns the "Hide devices with mappings" checkbox via the toggleHideMapped action,
 *   which calls the icon manager's setHideMappedDevices(checked).
 *
 * It resolves the icon manager instance via
 * window.Stimulus.getControllerForElementAndIdentifier(...) against the shared page
 * container, guarding for null (the manager may connect slightly after this controller)
 * and caching the last computed id set so it can be re-applied once the manager appears.
 */
export default class extends Controller {
    static stores = [areaStore]

    connect() {
        // Initialize instance state BEFORE useStore(this): useStore synchronously fires
        // onAreaStoreUpdate with the store's current value during connect(), and that
        // callback reads this._requestId / this._mappedIds — so they must exist first.
        this._requestId = 0
        this._mappedIds = []

        useStore(this)

        // Re-fetch on live mapping create/delete so colors + hide filter stay current.
        // Both events bubble up to this.element (the shared page container).
        this._onMappingChanged = this._handleMappingChanged.bind(this)
        this.element.addEventListener('floorplan:mapping-created', this._onMappingChanged)
        this.element.addEventListener('floorplan:mapping-deleted', this._onMappingChanged)
    }

    disconnect() {
        this.element.removeEventListener('floorplan:mapping-created', this._onMappingChanged)
        this.element.removeEventListener('floorplan:mapping-deleted', this._onMappingChanged)
    }

    // =========================================================================
    // STORE CALLBACKS
    // =========================================================================

    onAreaStoreUpdate(area) {
        this._loadMappedIds(area)
    }

    // =========================================================================
    // ACTIONS
    // =========================================================================

    /**
     * Toggle whether devices that already have mappings are hidden. Reads the checkbox
     * state and forwards it to the icon manager. Wired via
     * data-action="change->print-mapper--mapping-status#toggleHideMapped".
     */
    toggleHideMapped(event) {
        const checked = !!(event && event.target && event.target.checked)
        const manager = this._iconManager()
        if (manager) {
            manager.setHideMappedDevices(checked)
        }
    }

    // =========================================================================
    // EVENT HANDLERS
    // =========================================================================

    _handleMappingChanged(event) {
        // Both `floorplan:mapping-created` and `floorplan:mapping-deleted` carry the
        // fully-resolved updated workstation (with its current `print_mappings`) as
        // `event.detail.workstation` — the create/delete API response payload. That is
        // enough to flip exactly this one icon's mapped state LOCALLY, without a
        // full-area refetch (the regression was re-deriving the whole mapped-id set from
        // `GET /api/print_mappings/workstations?area_id=...` on every interaction).
        const detail = (event && event.detail) || {}
        const workstation = detail.workstation

        if (workstation && workstation.id) {
            // The workstation is mapped iff it has at least one print mapping. Add its id
            // to (or remove it from) the cached set, then re-push the set to the icon
            // manager, which recolors via setMappedDeviceIds -> scheduleRender (preserving
            // the coalesced render and the clause 3.1 green/red semantics).
            const isMapped = Array.isArray(workstation.print_mappings) && workstation.print_mappings.length > 0
            this._setMappedId(workstation.id, isMapped)
            this._applyMappedIds(this._mappedIds)
            return
        }

        // Fallback (no workstation payload on the event, e.g. an older dispatcher or an
        // error path): retain a single guarded refetch as a safety net so colors + the
        // hide filter still converge. The primary path above is local and refetch-free.
        this._loadMappedIds(this.areaStoreValue)
    }

    /**
     * Add or remove a single workstation id in the cached `this._mappedIds` array
     * (the array data structure `_loadMappedIds` and `_applyMappedIds` operate on).
     * When `isMapped` is true the id is added if not already present; when false it is
     * removed. A new array is assigned so the value is not mutated in place.
     */
    _setMappedId(id, isMapped) {
        const current = Array.isArray(this._mappedIds) ? this._mappedIds : []
        if (isMapped) {
            if (!current.includes(id)) {
                this._mappedIds = [...current, id]
            }
        } else {
            this._mappedIds = current.filter(existingId => existingId !== id)
        }
    }

    // =========================================================================
    // DATA
    // =========================================================================

    /**
     * Fetch the workstations for the given area and compute the set of ids that have at
     * least one print mapping, then push them to the icon manager. A request-id guard
     * discards stale responses when the area changes (or another event fires) mid-flight.
     */
    async _loadMappedIds(area) {
        const areaId = area && area.id
        const requestId = ++this._requestId

        if (!areaId) {
            this._mappedIds = []
            this._applyMappedIds([])
            return
        }

        try {
            const url = `/api/print_mappings/workstations?area_id=${encodeURIComponent(areaId)}`
            const response = await fetch(url, {
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`)
            }

            const data = await response.json()

            // Ignore stale responses if a newer request has since been issued.
            if (requestId !== this._requestId) return

            const workstations = (data && data.workstations) || []
            const mappedIds = workstations
                .filter(ws => Array.isArray(ws.print_mappings) && ws.print_mappings.length > 0)
                .map(ws => ws.id)

            this._mappedIds = mappedIds
            this._applyMappedIds(mappedIds)
        } catch (error) {
            if (requestId !== this._requestId) return
            console.error('print-mapper--mapping-status: failed to load mapped ids', error)
            this._mappedIds = []
            this._applyMappedIds([])
        }
    }

    /**
     * Push the mapped-id set to the icon manager so it recolors icons. If the manager
     * has not connected yet, retry shortly with the cached ids (guarded so a later,
     * fresher set is not clobbered by a stale retry).
     */
    _applyMappedIds(mappedIds) {
        const manager = this._iconManager()
        if (manager) {
            manager.setMappedDeviceIds(mappedIds)
            return
        }

        // The icon manager may connect slightly after this controller. Retry once the
        // event loop turns, applying the latest cached set (not this stale snapshot).
        setTimeout(() => {
            const lateManager = this._iconManager()
            if (lateManager) {
                lateManager.setMappedDeviceIds(this._mappedIds)
            }
        }, 100)
    }

    /**
     * Resolve the floor plan icon manager controller instance. The icon manager shares
     * this controller's root element on the VPM page; fall back to a document-wide
     * lookup in case the markup changes. Returns null when it is not (yet) connected.
     */
    _iconManager() {
        if (!window.Stimulus) return null

        const identifier = 'floorplans--floor-plan-icon-manager'
        let managerElement = this.element.matches(`[data-controller~="${identifier}"]`)
            ? this.element
            : this.element.closest(`[data-controller~="${identifier}"]`)

        if (!managerElement) {
            managerElement = document.querySelector(`[data-controller~="${identifier}"]`)
        }

        if (!managerElement) return null

        return window.Stimulus.getControllerForElementAndIdentifier(managerElement, identifier)
    }
}
